import React, { useState } from 'react';
import {
    Container, Paper, TextField, Button, Typography,
    Box, Alert, List, ListItem, ListItemText, Link,
    CircularProgress, Divider, Chip, Card, CardContent
} from '@mui/material';
import { Search as SearchIcon, Description as DescriptionIcon } from '@mui/icons-material';
import axios from 'axios';
import config from '../config/config';

const API_URL = config.apiURL;

function ConsultaCliente() {
    const [email, setEmail] = useState('');
    const [token, setToken] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [cliente, setCliente] = useState(null);
    const [processos, setProcessos] = useState([]);
    const [andamentos, setAndamentos] = useState([]);
    const [processoSelecionado, setProcessoSelecionado] = useState(null);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        setCliente(null);
        setProcessos([]);
        setAndamentos([]);
        setProcessoSelecionado(null);

        // Normaliza os valores antes de enviar
        const emailNormalizado = email.trim().toLowerCase();
        const tokenNormalizado = token.trim();

        if (!emailNormalizado || !tokenNormalizado) {
            setError('Por favor, preencha todos os campos');
            setLoading(false);
            return;
        }

        try {
            console.log('[CONSULTA] Tentando autenticar:', { email: emailNormalizado, tokenLength: tokenNormalizado.length });
            
            // Autentica o cliente
            const authResponse = await axios.post(`${API_URL}/consulta-cliente/auth`, {
                email: emailNormalizado,
                token: tokenNormalizado
            });

            console.log('[CONSULTA] Autenticação bem-sucedida:', authResponse.data);
            setCliente(authResponse.data);

            // Busca processos do cliente
            const processosResponse = await axios.post(`${API_URL}/consulta-cliente/processos`, {
                email: emailNormalizado,
                token: tokenNormalizado
            });

            console.log('[CONSULTA] Processos encontrados:', processosResponse.data?.length || 0);
            setProcessos(processosResponse.data || []);
        } catch (error) {
            console.error('[CONSULTA] Erro ao consultar:', error);
            console.error('[CONSULTA] Detalhes:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                url: error.config?.url
            });
            
            const errorMessage = error.response?.data?.error 
                || error.message 
                || 'Erro ao consultar. Verifique sua conexão e tente novamente.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleVerAndamentos = async (processoId) => {
        setLoading(true);
        setError('');
        try {
            // Normaliza os valores
            const emailNormalizado = email.trim().toLowerCase();
            const tokenNormalizado = token.trim();
            
            const response = await axios.post(`${API_URL}/consulta-cliente/processos/${processoId}/andamentos`, {
                email: emailNormalizado,
                token: tokenNormalizado
            });
            setAndamentos(response.data || []);
            setProcessoSelecionado(processos.find(p => p.id === processoId));
        } catch (error) {
            console.error('Erro ao buscar andamentos:', error);
            setError(error.response?.data?.error || 'Erro ao buscar andamentos');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (date) => {
        if (!date) return '-';
        try {
            // Se a data já vem formatada do backend (YYYY-MM-DD ou YYYY-MM-DD HH:mm:ss)
            if (typeof date === 'string') {
                // Formato: "2026-01-02" ou "2026-01-02 21:54:31"
                const match = date.match(/(\d{4})-(\d{2})-(\d{2})/);
                if (match) {
                    const [, ano, mes, dia] = match;
                    return `${dia}/${mes}/${ano}`;
                }
                // Se já está em formato brasileiro, retorna como está
                if (date.includes('/')) {
                    return date;
                }
            }
            
            // Fallback: tenta converter com Date
            const d = new Date(date);
            if (isNaN(d.getTime())) {
                return date; // Retorna a data original se não conseguir converter
            }
            
            const dia = String(d.getDate()).padStart(2, '0');
            const mes = String(d.getMonth() + 1).padStart(2, '0');
            const ano = d.getFullYear();
            
            return `${dia}/${mes}/${ano}`;
        } catch {
            return date || '-';
        }
    };

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper sx={{ p: 4 }}>
                <Box sx={{ mb: 4, textAlign: 'center' }}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Consulta de Processos
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Digite seu email e token para consultar seus processos
                    </Typography>
                </Box>

                {!cliente ? (
                    <Box component="form" onSubmit={handleLogin}>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={loading}
                            />
                            <TextField
                                fullWidth
                                label="Token"
                                value={token}
                                onChange={(e) => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                required
                                disabled={loading}
                                helperText="Token de 6 dígitos fornecido pelo escritório"
                                inputProps={{ maxLength: 6 }}
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                size="large"
                                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                                disabled={loading || !email || !token}
                            >
                                {loading ? 'Consultando...' : 'Consultar Processos'}
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Box>
                        <Alert severity="success" sx={{ mb: 3 }}>
                            Bem-vindo, <strong>{cliente.nome}</strong>!
                        </Alert>

                        {processos.length === 0 ? (
                            <Alert severity="info">
                                Você não possui processos cadastrados.
                            </Alert>
                        ) : (
                            <>
                                <Typography variant="h6" gutterBottom>
                                    Seus Processos
                                </Typography>
                                <List>
                                    {processos.map((processo, index) => (
                                        <React.Fragment key={processo.id}>
                                            <ListItem
                                                component={Card}
                                                sx={{ mb: 2 }}
                                            >
                                                <CardContent sx={{ width: '100%' }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                        <Box>
                                                            <Typography variant="h6" component="div">
                                                                {processo.numero_processo || 'Sem número'}
                                                            </Typography>
                                                            {processo.status && (
                                                                <Chip label={processo.status} size="small" sx={{ mt: 1, mr: 1 }} />
                                                            )}
                                                            {processo.tipo_beneficio && (
                                                                <Chip label={processo.tipo_beneficio} size="small" color="secondary" sx={{ mt: 1 }} />
                                                            )}
                                                        </Box>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<DescriptionIcon />}
                                                            onClick={() => handleVerAndamentos(processo.id)}
                                                            disabled={loading}
                                                        >
                                                            Ver Andamentos
                                                        </Button>
                                                    </Box>
                                                    <Box sx={{ mt: 1, fontSize: '0.875rem', color: 'text.secondary' }}>
                                                        {processo.vara && <Typography variant="body2">Vara: {processo.vara}</Typography>}
                                                        {processo.comarca && <Typography variant="body2">Comarca: {processo.comarca}</Typography>}
                                                        {processo.data_distribuicao && (
                                                            <Typography variant="body2">
                                                                Data de Distribuição: {formatDate(processo.data_distribuicao)}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </ListItem>
                                            {index < processos.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))}
                                </List>

                                {processoSelecionado && (
                                    <Box sx={{ mt: 4 }}>
                                        <Divider sx={{ mb: 2 }} />
                                        <Typography variant="h6" gutterBottom>
                                            Andamentos - {processoSelecionado.numero_processo || 'Sem número'}
                                        </Typography>
                                        {loading ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                                <CircularProgress />
                                            </Box>
                                        ) : andamentos.length === 0 ? (
                                            <Alert severity="info">
                                                Nenhum andamento disponível para visualização.
                                            </Alert>
                                        ) : (
                                            <List>
                                                {andamentos.map((andamento) => (
                                                    <Card key={andamento.id} sx={{ mb: 2 }}>
                                                        <CardContent>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                                <Typography variant="subtitle2" color="text.secondary">
                                                                    {formatDate(andamento.data_andamento)}
                                                                </Typography>
                                                                {andamento.tipo_andamento && (
                                                                    <Chip label={andamento.tipo_andamento} size="small" />
                                                                )}
                                                            </Box>
                                                            <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                                                                {andamento.descricao}
                                                            </Typography>
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </List>
                                        )}
                                    </Box>
                                )}
                            </>
                        )}

                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setCliente(null);
                                    setProcessos([]);
                                    setAndamentos([]);
                                    setProcessoSelecionado(null);
                                    setEmail('');
                                    setToken('');
                                }}
                            >
                                Nova Consulta
                            </Button>
                        </Box>
                    </Box>
                )}
            </Paper>
        </Container>
    );
}

export default ConsultaCliente;

